import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Activity } from '../services/activityService';
import { Calendar, Clock, User } from 'lucide-react';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;


const GanttContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  width: 100%;
  max-width: 100%;
  background: white;
`;

const GanttGrid = styled.div`
  display: flex;
  min-width: 1200px;
  width: max-content;
  height: fit-content;
`;

const TaskList = styled.div`
  background: #f8f9fa;
  border-right: 1px solid #e0e0e0;
  min-width: 350px;
  max-width: 350px;
  flex-shrink: 0;
  overflow: hidden;
`;

const TaskHeader = styled.div`
  background: #e9ecef;
  padding: 12px 16px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e0e0e0;
`;

const TaskRow = styled.div<{ isEven: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: ${props => props.isEven ? 'white' : '#f8f9fa'};
  min-height: 60px;
  max-height: 60px;
  overflow: hidden;
`;

const TaskInfo = styled.div`
  flex: 1;
  overflow: hidden;
  min-width: 0;
`;

const TaskTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9rem;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #666;
  flex-wrap: wrap;
  overflow: hidden;
`;

const TaskMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'in_progress': return '#cce5ff';
      case 'planned': return '#fff3cd';
      case 'cancelled': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'in_progress': return '#004085';
      case 'planned': return '#856404';
      case 'cancelled': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const PriorityBadge = styled.span<{ priority: string }>`
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${props => {
    switch (props.priority) {
      case 'urgent': return '#f8d7da';
      case 'high': return '#ffeaa7';
      case 'medium': return '#d1ecf1';
      case 'low': return '#d4edda';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.priority) {
      case 'urgent': return '#721c24';
      case 'high': return '#856404';
      case 'medium': return '#0c5460';
      case 'low': return '#155724';
      default: return '#495057';
    }
  }};
`;

const TimelineContainer = styled.div`
  position: relative;
  min-width: 800px;
  flex: 1;
  overflow: hidden;
`;

const TimelineHeader = styled.div`
  display: flex;
  background: #e9ecef;
  border-bottom: 1px solid #e0e0e0;
`;

const TimelineColumn = styled.div`
  flex: 1;
  padding: 12px 4px;
  text-align: center;
  font-weight: 600;
  color: #495057;
  border-right: 1px solid #e0e0e0;
  min-width: 60px;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TimelineRow = styled.div`
  position: relative;
  min-height: 60px;
  max-height: 60px;
  border-bottom: 1px solid #e0e0e0;
  overflow: hidden;
`;

const TaskBar = styled.div<{ 
  start: number; 
  duration: number; 
  status: string; 
  priority: string;
  progress: number;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.start}%;
  width: ${props => props.duration}%;
  height: 24px;
  border-radius: 4px;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#007bff';
      case 'planned': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  }};
  border: 2px solid ${props => {
    switch (props.priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-50%) scale(1.05);
    z-index: 10;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: rgba(255, 255, 255, 0.3);
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  z-index: 5;
`;

const Tooltip = styled.div<{ x: number; y: number }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -100%);
  margin-top: -8px;
  min-width: 200px;
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Legend = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: ${props => props.color};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #495057;
  margin: 16px 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
`;

interface GanttChartProps {
  activities: Activity[];
  viewMode?: 'week' | 'month' | 'quarter';
  onActivityClick?: (activity: Activity) => void;
}

const GanttChart: React.FC<GanttChartProps> = ({ 
  activities, 
  viewMode = 'week',
  onActivityClick 
}) => {
  const [hoveredActivity, setHoveredActivity] = useState<Activity | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Generate timeline dates based on view mode
  const timelineDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    const startDate = new Date(today);
    
    // Adjust start date based on view mode
    if (viewMode === 'week') {
      startDate.setDate(today.getDate() - today.getDay()); // Start of week
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date);
      }
    } else if (viewMode === 'month') {
      startDate.setDate(1); // Start of month
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0); // Last day of month
      
      for (let i = 0; i < endDate.getDate(); i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date);
      }
    } else if (viewMode === 'quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      startDate.setMonth(quarter * 3, 1);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 3);
      
      for (let i = 0; i < 90; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        if (date >= endDate) break;
        dates.push(date);
      }
    }
    
    return dates;
  }, [viewMode]);

  // Calculate task bar positions
  const getTaskBarPosition = (activity: Activity) => {
    const startDate = new Date(activity.startDate || activity.scheduledDate);
    const endDate = new Date(activity.endDate || activity.scheduledDate);
    const timelineStart = timelineDates[0];
    const timelineEnd = timelineDates[timelineDates.length - 1];
    
    if (!timelineStart || !timelineEnd) return { start: 0, duration: 0 };
    
    const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startDay = Math.max(0, Math.ceil((startDate.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
    const endDay = Math.min(totalDays - 1, Math.ceil((endDate.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
    
    const start = (startDay / totalDays) * 100;
    const duration = ((endDay - startDay + 1) / totalDays) * 100;
    
    return { start, duration };
  };

  const handleTaskHover = (activity: Activity, event: React.MouseEvent) => {
    setHoveredActivity(activity);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleTaskLeave = () => {
    setHoveredActivity(null);
  };

  // Show empty state if no activities
  if (activities.length === 0) {
    return (
      <Container>
        <Header>
          <Title>
            <Calendar size={20} />
            Activity Timeline
          </Title>
        </Header>

        <EmptyState>
          <Calendar size={48} />
          <EmptyStateTitle>No Activities Yet</EmptyStateTitle>
          <EmptyStateDescription>
            Start by adding your first activity in the calendar above. Once you create activities, 
            they will appear here as a visual timeline to help you track progress and dependencies.
          </EmptyStateDescription>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Calendar size={20} />
          Activity Timeline
        </Title>
      </Header>

      <GanttContainer>
        <GanttGrid>
          <TaskList>
            <TaskHeader>Tasks</TaskHeader>
            {activities.map((activity, index) => (
              <TaskRow key={activity.id} isEven={index % 2 === 0}>
                <TaskInfo>
                  <TaskTitle>{activity.title}</TaskTitle>
                  <TaskMeta>
                    <TaskMetaItem>
                      <User size={12} />
                      {activity.assignedTo}
                    </TaskMetaItem>
                    <TaskMetaItem>
                      <Clock size={12} />
                      {activity.estimatedDuration}h
                    </TaskMetaItem>
                    <StatusBadge status={activity.status}>
                      {activity.status.replace('_', ' ')}
                    </StatusBadge>
                    <PriorityBadge priority={activity.priority}>
                      {activity.priority}
                    </PriorityBadge>
                  </TaskMeta>
                </TaskInfo>
              </TaskRow>
            ))}
          </TaskList>

          <TimelineContainer>
            <TimelineHeader>
              {timelineDates.map((date, index) => (
                <TimelineColumn key={index}>
                  {date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </TimelineColumn>
              ))}
            </TimelineHeader>
            
            {activities.map((activity, index) => {
              const position = getTaskBarPosition(activity);
              return (
                <TimelineRow key={activity.id}>
                  <TaskBar
                    start={position.start}
                    duration={position.duration}
                    status={activity.status}
                    priority={activity.priority}
                    progress={activity.progress || 0}
                    onMouseEnter={(e) => handleTaskHover(activity, e)}
                    onMouseLeave={handleTaskLeave}
                    onClick={() => onActivityClick?.(activity)}
                  >
                    <ProgressText>{activity.progress}%</ProgressText>
                  </TaskBar>
                </TimelineRow>
              );
            })}
          </TimelineContainer>
        </GanttGrid>
      </GanttContainer>

      {hoveredActivity && (
        <Tooltip x={tooltipPosition.x} y={tooltipPosition.y}>
          <TooltipTitle>{hoveredActivity.title}</TooltipTitle>
          <TooltipContent>
            <div>Assigned to: {hoveredActivity.assignedTo}</div>
            <div>Duration: {hoveredActivity.estimatedDuration}h</div>
            <div>Progress: {hoveredActivity.progress}%</div>
            <div>Location: {hoveredActivity.location}</div>
            {hoveredActivity.startTime && (
              <div>Time: {hoveredActivity.startTime} - {hoveredActivity.endTime}</div>
            )}
          </TooltipContent>
        </Tooltip>
      )}

      <Legend>
        <LegendItem>
          <LegendColor color="#28a745" />
          <span>Completed</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#007bff" />
          <span>In Progress</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#ffc107" />
          <span>Planned</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#dc3545" />
          <span>Cancelled</span>
        </LegendItem>
      </Legend>
    </Container>
  );
};

export default GanttChart;
